import {Fragment, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {CheckIcon, EnvelopeOpenIcon} from '@heroicons/react/24/outline';
import {cognitoConfirmSignUp, cognitoResendConfirmationCode} from './cognito';
import {Button} from '../../components/Button';

export interface VerificationModalProps {
  email: string;
  loginFunc: () => Promise<void>;
  clientId: string;
  region: string;
}

export default function VerificationModal(props: VerificationModalProps) {
  const [open, setOpen] = useState(true);
  const [verificationDone, setVerificationDone] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const confirmEmail = async (verificationCode: string) => {
    const success = await cognitoConfirmSignUp(
      props.region,
      props.clientId,
      props.email,
      verificationCode,
    );
    if (!success) {
      alert('Confirmation code incorrect. Please try again.');
      return;
    }
    setVerificationDone(true);
  };

  const resendVerificationCode = async () => {
    setResending(true);
    setResendMessage('');
    const success = await cognitoResendConfirmationCode(props.region, props.clientId, props.email);
    if (success) {
      setResendMessage('Code sent!');
    } else {
      setResendMessage('Failed to resend. Please try again.');
    }
    setResending(false);
  };

  const loginAndClose = async () => {
    setLoggingIn(true);
    await props.loginFunc();
    setOpen(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          /* Prevent closing by clicking outside if needed, or setOpen(false) */
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-surface px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div
                    className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${
                      verificationDone ? 'bg-success-subtle' : 'bg-subtle'
                    }`}
                  >
                    {verificationDone ? (
                      <CheckIcon className="h-8 w-8 text-success" aria-hidden="true" />
                    ) : (
                      <EnvelopeOpenIcon className="h-8 w-8 text-brand" aria-hidden="true" />
                    )}
                  </div>
                  <div className="mt-4 text-center sm:mt-6">
                    <Dialog.Title as="h3" className="text-lg  leading-6 text-main">
                      {verificationDone ? 'Email Verified' : 'Check Your Inbox'}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm  text-muted">
                        {verificationDone
                          ? 'All set! Welcome to TripVibes.'
                          : `We've sent a 6-digit code to ${props.email}. Enter it below to verify your account.`}
                      </p>
                    </div>
                  </div>
                </div>

                {!verificationDone && (
                  <div className="mt-6">
                    <input
                      type="text"
                      className="block w-full rounded-2xl border-0 bg-subtle py-3 text-center text-2xl  tracking-[0.5em] text-main shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-teal-600 transition-all placeholder-text-disabled"
                      placeholder="000000"
                      maxLength={6}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        e.target.value = val;
                        if (val.length === 6) confirmEmail(val);
                      }}
                    />
                    <div className="mt-4 text-center">
                      <p className="text-sm  text-muted">
                        No code?{' '}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={resendVerificationCode}
                          disabled={resending}
                          className="hover:underline p-0 h-auto"
                        >
                          {resending ? 'Sending...' : 'Send again'}
                        </Button>
                      </p>
                      {resendMessage && (
                        <p
                          className={`mt-2 text-xs  ${
                            resendMessage.includes('Failed') ? 'text-error' : 'text-success'
                          }`}
                        >
                          {resendMessage}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {verificationDone && (
                  <div className="mt-6 sm:mt-8">
                    <Button
                      type="button"
                      variant="primary"
                      fullWidth
                      onClick={loginAndClose}
                      disabled={loggingIn}
                      loading={loggingIn}
                    >
                      {loggingIn ? 'Logging in...' : 'Continue to TripVibes'}
                    </Button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
